import { QueryClient } from '@tanstack/react-query';
import { notifyUser } from '../util/util';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Time in milliseconds that unused/inactive cache data remains in memory
      staleTime: 5 * 60 * 1000, // 5 minutes
      
      // Time in milliseconds that cache data remains in memory after being unused
      cacheTime: 10 * 60 * 1000, // 10 minutes
      
      // Retry failed requests
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      
      // Retry delay
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch on window focus
      refetchOnWindowFocus: false,
      
      // Refetch on reconnect
      refetchOnReconnect: true,
      
      // Background refetch interval
      refetchInterval: false,
      
      // Error handling
      onError: (error) => {
        // Only show error notifications for non-authentication errors
        if (error?.response?.status !== 401) {
          const message = error?.response?.data?.message || 
                         error?.message || 
                         'An unexpected error occurred';
          notifyUser('error', message, 'Error');
        }
      },
    },
    mutations: {
      // Retry failed mutations
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 2 times for other errors
        return failureCount < 2;
      },
      
      // Error handling for mutations
      onError: (error) => {
        if (error?.response?.status !== 401) {
          const message = error?.response?.data?.message || 
                         error?.message || 
                         'An unexpected error occurred';
          notifyUser('error', message, 'Error');
        }
      },
    },
  },
});

// Query keys factory for better organization
export const queryKeys = {
  // Authentication
  auth: {
    user: ['auth', 'user'],
    permissions: ['auth', 'permissions'],
  },
  
  // Dashboard
  dashboard: {
    stats: ['dashboard', 'stats'],
    recentActivities: ['dashboard', 'recent-activities'],
  },
  
  // Companies
  companies: {
    all: ['companies'],
    list: (filters) => ['companies', 'list', filters],
    detail: (id) => ['companies', 'detail', id],
    contracts: (id) => ['companies', id, 'contracts'],
    quotes: (id) => ['companies', id, 'quotes'],
  },
  
  // Contracts
  contracts: {
    all: ['contracts'],
    list: (filters) => ['contracts', 'list', filters],
    detail: (id) => ['contracts', 'detail', id],
    history: (id) => ['contracts', id, 'history'],
    documents: (id) => ['contracts', id, 'documents'],
  },
  
  // Quotes
  quotes: {
    all: ['quotes'],
    list: (filters) => ['quotes', 'list', filters],
    detail: (id) => ['quotes', 'detail', id],
    templates: ['quotes', 'templates'],
  },
  
  // Schedules
  schedules: {
    all: ['schedules'],
    list: (filters) => ['schedules', 'list', filters],
    detail: (id) => ['schedules', 'detail', id],
    upload: ['schedules', 'upload'],
    validation: (uploadId) => ['schedules', 'validation', uploadId],
  },
  
  // Audit Logs
  auditLogs: {
    all: ['audit-logs'],
    list: (filters) => ['audit-logs', 'list', filters],
  },
  
  // Settings
  settings: {
    profile: ['settings', 'profile'],
    preferences: ['settings', 'preferences'],
    system: ['settings', 'system'],
  },

  // Biodata
  biodata: ['biodata'],

  // Financial Insights
  financialInsights: {
    contents: (filters) => ['financialInsights', 'contents', filters],
    content: (id) => ['financialInsights', 'content', id],
    contentBySlug: (slug) => ['financialInsights', 'content', 'slug', slug],
    categories: ['financialInsights', 'categories'],
    category: (id) => ['financialInsights', 'category', id],
    bookmarks: ['financialInsights', 'bookmarks'],
    bookmark: (id) => ['financialInsights', 'bookmark', id],
  },
};
